import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import NavBar from "@/components/NavBar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { authApi, type AdminUser, type AdminUserDetail } from "@/lib/authApi";
import Pagination from "@/components/ui/pagination";
import {
  ArrowLeft,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Users,
  Shield,
  UserCheck,
  UserX,
  RefreshCw,
  Download,
  Filter,
  Eye,
  Mail,
  Calendar,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react";

const AdminUsers: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(
    null
  );
  const [isUserDetailOpen, setIsUserDetailOpen] = useState(false);

  // Check if user is admin - this must come after all hooks
  const isAdmin = isAuthenticated && user?.role === "ADMIN";

  // Fetch users
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-users", page, limit, search, roleFilter],
    queryFn: () => authApi.getUsers(page, limit, search, roleFilter),
    enabled: isAdmin,
  });

  const users = usersData?.users || [];
  const pagination = usersData?.pagination;

  // Mutations
  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      authApi.updateUserRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({
        title: "Role updated",
        description: "User role has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update user role. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => authApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({
        title: "User deleted",
        description: "User has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    },
  });

  const bulkActionMutation = useMutation({
    mutationFn: ({
      action,
      value,
    }: {
      action: string;
      value?: string | boolean;
    }) => authApi.bulkUserAction(selectedUsers, action, value),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelectedUsers([]);
      toast({
        title: "Bulk action completed",
        description: `${data.successful} users updated successfully.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to perform bulk action. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Fetch user detail
  const fetchUserDetail = async (id: string) => {
    try {
      const userDetail = await authApi.getUserById(id);
      setSelectedUser(userDetail);
      setIsUserDetailOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch user details.",
        variant: "destructive",
      });
    }
  };

  // Handle bulk actions
  const handleBulkAction = (action: string, value?: string | boolean) => {
    if (selectedUsers.length === 0) {
      toast({
        title: "No users selected",
        description: "Please select users to perform bulk actions.",
        variant: "destructive",
      });
      return;
    }

    bulkActionMutation.mutate({ action, value });
  };

  // Handle user selection
  const handleUserSelection = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(users.map((user) => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      ADMIN: { color: "bg-red-100 text-red-800", icon: Shield },
      CREATOR: { color: "bg-blue-100 text-blue-800", icon: UserCheck },
      USER: { color: "bg-green-100 text-green-800", icon: Users },
    };

    const config =
      roleConfig[role as keyof typeof roleConfig] || roleConfig.USER;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {role}
      </Badge>
    );
  };

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                You need admin privileges to access user management.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-4 sm:mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm sm:text-base"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-r from-primary to-accent">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    User Management
                  </h1>
                  <p className="text-sm sm:text-base text-muted-foreground mt-1">
                    Manage users, roles, and permissions
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    queryClient.invalidateQueries({ queryKey: ["admin-users"] })
                  }
                  className="text-xs sm:text-sm"
                >
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Refresh</span>
                  <span className="sm:hidden">Refresh</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Export</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-4 sm:mb-6"
          >
            <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between">
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-1 w-full">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        placeholder="Search users by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 text-sm"
                      />
                    </div>
                    <Select
                      value={roleFilter}
                      onValueChange={(value) => setRoleFilter(value)}
                    >
                      <SelectTrigger className="w-full sm:w-48 text-sm">
                        <SelectValue placeholder="Filter by role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="ADMIN">Admin</SelectItem>
                        <SelectItem value="CREATOR">Creator</SelectItem>
                        <SelectItem value="USER">User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2 w-full xl:w-auto">
                      <Select
                        onValueChange={(value) =>
                          handleBulkAction("updateRole", value)
                        }
                      >
                        <SelectTrigger className="w-full sm:w-40 text-sm">
                          <SelectValue placeholder="Change Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USER">User</SelectItem>
                          <SelectItem value="CREATOR">Creator</SelectItem>
                          <SelectItem value="ADMIN">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleBulkAction("delete")}
                        className="text-xs sm:text-sm flex-1 sm:flex-none"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">
                          Delete ({selectedUsers.length})
                        </span>
                        <span className="sm:hidden">Delete</span>
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Users Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="shadow-xl bg-card/50 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-muted/50 to-transparent border-b border-border/50">
                <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <span className="text-lg sm:text-xl">
                      Users ({usersData?.pagination.total || 0})
                    </span>
                    {pagination && (
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        Showing {(pagination.page - 1) * pagination.limit + 1}{" "}
                        to{" "}
                        {Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )}{" "}
                        of {pagination.total}
                      </span>
                    )}
                  </div>
                  {selectedUsers.length > 0 && (
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      {selectedUsers.length} selected
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-6 sm:p-8 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary mx-auto mb-3 sm:mb-4"></div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      Loading users...
                    </p>
                  </div>
                ) : error ? (
                  <div className="p-6 sm:p-8 text-center">
                    <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-3 sm:mb-4" />
                    <p className="text-red-600 text-sm sm:text-base">
                      Failed to load users
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8 sm:w-12">
                            <Checkbox
                              checked={
                                selectedUsers.length ===
                                  (usersData?.users.length || 0) &&
                                selectedUsers.length > 0
                              }
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead className="text-xs sm:text-sm">
                            User
                          </TableHead>
                          <TableHead className="text-xs sm:text-sm">
                            Role
                          </TableHead>
                          <TableHead className="text-xs sm:text-sm hidden lg:table-cell">
                            Campaigns
                          </TableHead>
                          <TableHead className="text-xs sm:text-sm hidden lg:table-cell">
                            Engagement
                          </TableHead>
                          <TableHead className="text-xs sm:text-sm hidden md:table-cell">
                            Joined
                          </TableHead>
                          <TableHead className="text-xs sm:text-sm w-8 sm:w-12">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onCheckedChange={(checked) =>
                                  handleUserSelection(
                                    user.id,
                                    checked as boolean
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className="text-xs sm:text-sm">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-white text-xs sm:text-sm font-semibold">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate">
                                    {user.name}
                                  </div>
                                  <div className="text-xs sm:text-sm text-muted-foreground truncate">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs sm:text-sm">
                              {getRoleBadge(user.role)}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="flex items-center gap-1">
                                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                                <span className="font-medium text-xs sm:text-sm">
                                  {user._count.campaigns}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                                <span className="font-medium text-xs sm:text-sm">
                                  {user._count.comments + user._count.responses}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-xs sm:text-sm">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                                <span className="text-xs sm:text-sm">
                                  {new Date(
                                    user.createdAt
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs sm:text-sm">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                  >
                                    <MoreHorizontal className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => fetchUserDetail(user.id)}
                                    className="text-xs sm:text-sm"
                                  >
                                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate(`/admin/users/${user.id}/edit`)
                                    }
                                    className="text-xs sm:text-sm"
                                  >
                                    <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                                    Edit User
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      deleteUserMutation.mutate(user.id)
                                    }
                                    className="text-red-600 text-xs sm:text-sm"
                                  >
                                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                                    Delete User
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Pagination */}
          {pagination && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 sm:mt-6"
            >
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={setPage}
              />
            </motion.div>
          )}
        </div>
      </div>

      {/* User Detail Dialog */}
      <Dialog open={isUserDetailOpen} onOpenChange={setIsUserDetailOpen}>
        <DialogContent className="max-w-2xl mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              User Details
            </DialogTitle>
            <DialogDescription className="text-sm">
              Detailed information about the selected user.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                    Name
                  </label>
                  <p className="text-base sm:text-lg font-semibold">
                    {selectedUser.name}
                  </p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <p className="text-base sm:text-lg break-all">
                    {selectedUser.email}
                  </p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                    Role
                  </label>
                  <div className="mt-1">{getRoleBadge(selectedUser.role)}</div>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                    Joined
                  </label>
                  <p className="text-base sm:text-lg">
                    {new Date(selectedUser.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="text-center p-3 sm:p-4 bg-muted rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-primary">
                    {selectedUser._count.campaigns}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    Campaigns
                  </div>
                </div>
                <div className="text-center p-3 sm:p-4 bg-muted rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-primary">
                    {selectedUser._count.comments}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    Comments
                  </div>
                </div>
                <div className="text-center p-3 sm:p-4 bg-muted rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-primary">
                    {selectedUser._count.responses}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    Responses
                  </div>
                </div>
              </div>

              {selectedUser.campaigns.length > 0 && (
                <div>
                  <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                    Recent Campaigns
                  </label>
                  <div className="mt-2 space-y-2">
                    {selectedUser.campaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-muted rounded-lg gap-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm sm:text-base truncate">
                            {campaign.title}
                          </div>
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            {campaign.status}
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground">
                          {campaign._count.responses} responses
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
